import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import api, { formatApiError } from '../lib/api';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8 space-y-6">
          <div className="flex justify-center mb-2">
            <img src="/csg-logo.png" alt="CSG Security" className="h-10 w-auto" />
          </div>

          {!sent ? (
            <>
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">Reset your password</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Enter your account email and we'll send you a link to set a new password.
                </p>
              </div>

              {error && (
                <Alert variant="destructive" data-testid="forgot-error">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wide text-slate-600">
                    Email address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@csgconnect.co.za"
                      className="pl-9"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#80C030] hover:bg-[#6FA528] text-white font-semibold"
                  disabled={submitting}
                  data-testid="send-reset-link-btn"
                >
                  {submitting ? 'Sending link...' : 'Send reset link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4" data-testid="forgot-success">
              <div className="w-14 h-14 mx-auto bg-[#80C030]/15 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#0D5B69]" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">Check your email</h2>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                  If an account exists for <span className="font-medium text-slate-700">{email}</span>,
                  we've sent a password reset link. The link expires in 1 hour.
                </p>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-slate-100 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-xs text-[#0D5B69] hover:underline font-semibold"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
